import * as React from "react";
import { useLayoutEffect, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import PersonIcon from "@material-ui/icons/Person";
import { ImpersonationDialog } from "./ImpersonationDialog";
import {
  ImpersonatedTechnician,
  QA_IMPERSONATION_ENABLED,
  readImpersonation,
  writeImpersonation,
} from "../../lib/qaImpersonation";

/**
 * QA-IMPERSONATION — sits above every app bar so it is impossible to forget which
 * technician's data is on screen.
 *
 * Blue means you are looking at your own data; orange means you are not. The colour is
 * the whole point of the banner: a QA screenshot of someone else's schedule should
 * never be mistakable for your own.
 */
export const ImpersonationBanner: React.FC = () => {
  const client = useApolloClient();
  const [technician, setTechnician] = useState<ImpersonatedTechnician | null>(() => readImpersonation());
  const [dialogOpen, setDialogOpen] = useState(false);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  /**
   * The banner sticks to the top, and so do the app bars — both at top: 0, so without
   * this the banner would simply paint over them. Publishes its own measured height as
   * a custom property and flags the root, so the sticky headers can offset themselves
   * (see styles.css). Measured rather than hard-coded: the label wraps to two lines on
   * a narrow phone with a long technician name.
   */
  useLayoutEffect(() => {
    if (!QA_IMPERSONATION_ENABLED) return undefined;
    const root = document.documentElement;
    const publish = () => root.style.setProperty("--qa-banner-height", `${bannerRef.current?.offsetHeight ?? 0}px`);
    publish();
    root.classList.add("qa-impersonation-on");
    window.addEventListener("resize", publish);
    return () => {
      window.removeEventListener("resize", publish);
      root.classList.remove("qa-impersonation-on");
      root.style.removeProperty("--qa-banner-height");
    };
  });

  if (!QA_IMPERSONATION_ENABLED) return null;

  /**
   * Every cached query was answered for the previous technician, so the store has to
   * go with the switch — otherwise the schedule shows one technician's jobs under
   * another's name until something happens to refetch.
   */
  const apply = async (next: ImpersonatedTechnician | null) => {
    writeImpersonation(next);
    setTechnician(next);
    setDialogOpen(false);
    try {
      await client.resetStore();
    } catch {
      // resetStore rejects if an in-flight query is cancelled; the cache is cleared
      // either way, so there is nothing to recover.
    }
  };

  const impersonating = !!technician;
  const background = impersonating ? "#c2410c" : "#1d4ed8";

  return (
    <>
      <div
        ref={bannerRef}
        className="w-full flex items-center justify-between gap-2 px-3 py-1 sticky top-0"
        style={{ backgroundColor: background, color: "#ffffff", zIndex: 30 }}
        role="status"
      >
        <span className="flex items-center gap-2 min-w-0">
          <PersonIcon style={{ fontSize: 18, flexShrink: 0 }} />
          <Typography variant="body2" noWrap style={{ fontWeight: 600 }}>
            {impersonating ? `Viewing as ${technician.name} · ${technician.market}` : "Viewing as yourself"}
          </Typography>
        </span>
        <Button
          size="small"
          onClick={() => setDialogOpen(true)}
          style={{ textTransform: "none", color: "#ffffff", fontWeight: 600, flexShrink: 0 }}
        >
          Change
        </Button>
      </div>

      <ImpersonationDialog
        open={dialogOpen}
        current={technician}
        onClose={() => setDialogOpen(false)}
        onSelect={(next) => apply(next)}
        onStop={() => apply(null)}
      />
    </>
  );
};
